/**
 * Purchase Confirmation Modal - Enhanced UX showing all transaction details
 * PRICE FREEZE: When modal opens, EMON rate is frozen for the entire transaction
 */

import { useState, useEffect, useRef } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import {
  Coins,
  DollarSign,
  ArrowRight,
  TrendingUp,
  User,
  CheckCircle,
  ShoppingCart,
  Loader2,
  Lock,
} from "lucide-react";
import { useEmonRate } from "@/contexts/EmonPriceContext";
import { useSolPrice } from "@/hooks/useSolPrice";

interface PurchaseConfirmationModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onConfirm: (frozenEmonRate: number, frozenSolPrice: number) => void;
  loading: boolean;
  nft: {
    name: string;
    image_url: string | null;
    owner_wallet: string;
    owner_name: string | null;
  };
  priceUsd: number;
}

// Bonding curve formula (display only - actual calculation is backend)
function calculateNextPrice(currentPrice: number): number {
  return currentPrice * 1.25 + 0.01;
}

export function PurchaseConfirmationModal({
  open,
  onOpenChange,
  onConfirm,
  loading,
  nft,
  priceUsd,
}: PurchaseConfirmationModalProps) {
  const { rate: liveEmonRate, usdToEmon: liveUsdToEmon } = useEmonRate();
  const { price: liveSolPrice } = useSolPrice();
  
  // FROZEN prices - captured when modal opens
  const [frozenEmonRate, setFrozenEmonRate] = useState<number | null>(null);
  const [frozenSolPrice, setFrozenSolPrice] = useState<number | null>(null);
  const [frozenAt, setFrozenAt] = useState<Date | null>(null);
  
  // Freeze prices when modal opens
  useEffect(() => {
    if (open && frozenEmonRate === null) {
      setFrozenEmonRate(liveEmonRate);
      setFrozenSolPrice(liveSolPrice);
      setFrozenAt(new Date());
      console.log(`🔒 [PRICE FREEZE] EMON: $${liveEmonRate.toFixed(8)}, SOL: $${liveSolPrice.toFixed(2)}`);
    }
    
    // Reset frozen prices when modal closes
    if (!open) {
      setFrozenEmonRate(null);
      setFrozenSolPrice(null);
      setFrozenAt(null);
    }
  }, [open, liveEmonRate, liveSolPrice, frozenEmonRate]);

  // Use frozen rate for calculations, fall back to live if not frozen yet
  const activeEmonRate = frozenEmonRate ?? liveEmonRate;
  const activeSolPrice = frozenSolPrice ?? liveSolPrice;
  
  // Calculate with frozen rate
  const usdToEmonFrozen = (usd: number) => {
    if (activeEmonRate <= 0) return 0;
    return usd / activeEmonRate;
  };
  
  const emonAmount = usdToEmonFrozen(priceUsd);
  const BURN_FEE_EMON = 100; // Display only
  const totalEmonWithFee = emonAmount + BURN_FEE_EMON;
  const nextPriceUsd = calculateNextPrice(priceUsd);
  const nextPriceEmon = usdToEmonFrozen(nextPriceUsd);

  const shortWallet = (wallet: string) =>
    `${wallet.slice(0, 4)}...${wallet.slice(-4)}`;

  const handleConfirm = () => {
    if (frozenEmonRate !== null && frozenSolPrice !== null) {
      onConfirm(frozenEmonRate, frozenSolPrice);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ShoppingCart className="w-5 h-5 text-primary" />
            Confirm Purchase
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          {/* NFT Preview */}
          <div className="flex items-center gap-4 p-3 rounded-lg bg-muted/50">
            {nft.image_url ? (
              <img
                src={nft.image_url}
                alt={nft.name}
                className="w-16 h-16 rounded-lg object-cover"
              />
            ) : (
              <div className="w-16 h-16 rounded-lg bg-muted flex items-center justify-center">
                <Coins className="w-6 h-6 text-muted-foreground" />
              </div>
            )}
            <div className="flex-1 min-w-0">
              <p className="font-semibold truncate">{nft.name}</p>
              <p className="text-xs text-muted-foreground flex items-center gap-1">
                <User className="w-3 h-3" />
                Seller: {nft.owner_name || shortWallet(nft.owner_wallet)}
              </p>
            </div>
          </div>

          <Separator />

          {/* Price Breakdown */}
          <div className="space-y-3">
            <div className="flex justify-between items-center">
              <span className="text-sm text-muted-foreground">NFT Price (USD)</span>
              <span className="font-bold text-lg flex items-center gap-1">
                <DollarSign className="w-4 h-4 text-green-500" />
                {priceUsd.toFixed(2)}
              </span>
            </div>

            <div className="flex justify-between items-center">
              <span className="text-sm text-muted-foreground">NFT Price (EMON)</span>
              <span className="font-medium flex items-center gap-1">
                <Coins className="w-4 h-4 text-primary" />
                {emonAmount.toLocaleString(undefined, { maximumFractionDigits: 0 })}
              </span>
            </div>

            <div className="flex justify-between items-center text-orange-500">
              <span className="text-sm">+ Platform Fee</span>
              <span className="text-sm font-medium">
                {BURN_FEE_EMON} EMON
              </span>
            </div>

            <Separator className="my-2" />

            <div className="flex justify-between items-center">
              <span className="text-sm font-semibold">Total You Pay</span>
              <span className="font-bold text-lg flex items-center gap-1 text-primary">
                <Coins className="w-4 h-4" />
                {totalEmonWithFee.toLocaleString(undefined, { maximumFractionDigits: 0 })} EMON
              </span>
            </div>

            <div className="flex justify-between items-center">
              <span className="text-sm text-muted-foreground">Seller Receives</span>
              <span className="text-sm font-medium text-green-500">
                {emonAmount.toLocaleString(undefined, { maximumFractionDigits: 0 })} EMON
              </span>
            </div>
          </div>

          <Separator />

          {/* Next Price Preview */}
          <div className="p-3 rounded-lg bg-primary/5 border border-primary/20">
            <div className="flex items-start gap-2">
              <TrendingUp className="w-5 h-5 text-primary mt-0.5" />
              <div className="flex-1">
                <p className="text-sm font-medium">Bonding Curve</p>
                <p className="text-xs text-muted-foreground mb-2">
                  Price increases after each sale (×1.25 + $0.01)
                </p>
                <div className="flex items-center gap-2 text-sm">
                  <Badge variant="outline" className="gap-1">
                    <DollarSign className="w-3 h-3" />
                    {priceUsd.toFixed(2)}
                  </Badge>
                  <ArrowRight className="w-4 h-4 text-primary" />
                  <Badge className="bg-green-500/10 text-green-500 border-green-500/30 gap-1">
                    <DollarSign className="w-3 h-3" />
                    {nextPriceUsd.toFixed(2)}
                  </Badge>
                </div>
                <p className="text-[10px] text-muted-foreground mt-2">
                  Next buyer pays ~{nextPriceEmon.toLocaleString(undefined, { maximumFractionDigits: 0 })} EMON
                </p>
              </div>
            </div>
          </div>

          {/* Frozen Rate Info */}
          <div className="flex items-center justify-between text-xs p-2 rounded bg-amber-500/10 border border-amber-500/20">
            <span className="flex items-center gap-1 text-amber-600">
              <Lock className="w-3 h-3" />
              Rate Locked
            </span>
            <span className="text-amber-600 font-mono">
              1 EMON = ${activeEmonRate.toFixed(8)}
            </span>
          </div>
        </div>

        <DialogFooter className="flex-col sm:flex-row gap-2">
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={loading}
            className="w-full sm:w-auto"
          >
            Cancel
          </Button>
          <Button
            onClick={handleConfirm}
            disabled={loading || frozenEmonRate === null}
            className="w-full sm:w-auto gap-2"
          >
            {loading ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                Processing...
              </>
            ) : (
              <>
                <CheckCircle className="w-4 h-4" />
                Confirm Purchase
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
